import { useState, useEffect } from 'react';
import '../styles/pages/Product.scss';
import SERVER from '../hooks/useServer';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useCart } from '../hooks/cart';
import { useWishlist } from '../hooks/wishlist';
import {formatPrice} from '../hooks/generalHooks';
import { FaFilePdf, FaHeart, FaRegHeart } from "react-icons/fa";
import { IoChevronForward, IoChevronDown } from "react-icons/io5";
import { PiShoppingCartSimple } from "react-icons/pi";
import { PiInfo, PiInfoFill } from "react-icons/pi";
import { FaCheck } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';

const InfoIconWithTooltip = () => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div style={{ position: 'relative', display: 'flex' }}>
      {/* Icon container */}
      <div 
        style={{ position: 'relative', display: 'inline-flex', border : 'none' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <PiInfoFill style={{ color: '#E5E5E5' }} size={18} />
        <PiInfo size={18} style={{ 
          color: '#1B66A4',
          position: 'absolute',
          top: 0,
          left: 0
        }} />
      </div>
      
      {/* Tooltip */}
      {isHovered && (
        <div style={{
            position: 'absolute',
            top: '100%',
            right: window.innerWidth <= 768 ? '50%' : '0%',
            transform: window.innerWidth <= 768 ? 'translateX(50%)' : 'translateX(87.5%)',
            marginTop: '8px',
            padding: '6px 12px',
            backgroundColor: '#F2F3F4',
            color: 'black',
            borderRadius: '4px',
            fontSize: window.innerWidth <= 768 ? '12px' : '14px',
            whiteSpace: window.innerWidth <= 768 ? 'normal' : 'nowrap',
            zIndex: 100,
            boxShadow: '0 2px 8px #F2F3F4',
            maxWidth: window.innerWidth <= 768 ? '200px' : 'none',
            width: window.innerWidth <= 768 ? '244px' : 'none',
            textAlign: window.innerWidth <= 768 ? 'center' : 'left'
        }}>
            {t('product.calibrationTooltip')}
            <div style={{
                position: 'absolute',
                bottom: '100%',
                left: window.innerWidth <= 768 ? '50%' : '10%',
                transform: window.innerWidth <= 768 ? 'translateX(-50%)' : 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: '6px solid #F2F3F4'
            }} />
        </div>
        )}
    </div>
  );
};

const Product = () => {
    const { t, i18n } = useTranslation();
    const { addToCart } = useCart();
    const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
    const navigate = useNavigate();
    const { state } = useLocation();
    const {categoryTitle, subCategoryTitle} = useParams()
    const { fetchProduct } = useApi();
    const categoryId = state?.categoryId;
    const subCategoryId = state?.subCategoryId;  
    const productId = state?.productId  
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('caracteristique');
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [calibrage, setCalibrage] = useState(false)
    const [chosenCalibrage, setChosenCalibrage] = useState(false)
    const [quantite, setQuantite] = useState(1)
    const [selectedImage, setSelectedImage] = useState(null);

    const toggleWishlist = () => {
        if (isInWishlist(selectedProduct.id)) {
            removeFromWishlist(selectedProduct.id);
        } else {
            addToWishlist({
                ...selectedProduct,
                category_title: categoryTitle,
                subcategory_title: subCategoryTitle,
                category_id: categoryId,
                subcategory_id: subCategoryId
            });
        }
    };

    const updateQuantity = (num) => {
        setQuantite(num);
    };

    useEffect(() => {
        const loadProduct = async () => {
            try {
                setLoading(true);
                const productData = await fetchProduct(productId);
                if (productData) {
                    setSelectedProduct(productData);
                } else {
                    setError('Product not found');
                }
            } catch (err) {
                setError(err.message);
                console.error('Error fetching product:', err);
            } finally {
                setLoading(false);
            }
        };

        if (productId) {
            loadProduct();
        }
    }, [productId, i18n.language, fetchProduct]);
    
    if (loading) return <div className="flex items-center justify-center min-h-screen">{t('common.loading')}</div>;
    if (error) return <div className="flex items-center justify-center min-h-screen text-red-500">{t('common.error')}: {error}</div>;

    return (
        <div className="w-full max-w-7xl mx-auto px-3 lg:px-4 py-4 lg:py-8 pt-20 lg:pt-24">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 lg:gap-2 text-xs lg:text-sm mb-4 lg:mb-8 flex-wrap">
                <span className='cursor-pointer text-black hover:underline' onClick={() => navigate('/')}>{t('breadcrumbs.home')}</span>
                <IoChevronForward className="text-gray-400 text-xs lg:text-sm" />
                <span className='text-gray-600'>{t('product.categories')}</span>
                {categoryTitle && (
                    <>
                        <IoChevronForward className="text-gray-400 text-xs lg:text-sm" />
                        <span className='text-gray-600'>{categoryTitle}</span>
                    </>
                )}
                {subCategoryTitle && (
                    <>
                        <IoChevronForward className="text-gray-400 text-xs lg:text-sm" />
                        <span className='cursor-pointer text-black hover:underline' 
                                onClick={() => {
                                navigate(`/products/${categoryTitle}/${subCategoryTitle}`, { state: { categoryId: categoryId, subCategoryId : subCategoryId } });
                                }}> 
                            {subCategoryTitle} 
                        </span>
                    </>
                )}
            </div>
            {selectedProduct && (
                <div className='w-full'>
                    {/* Main Product Section */}
                    <div className='flex flex-col lg:flex-row gap-4 lg:gap-8 mb-8 lg:mb-12 bg-white rounded-xl p-4 lg:p-8 shadow-sm'>
                        {/* Left: Product Images */}
                        <div className='w-full lg:w-1/2 flex-shrink-0'>
                            {/* Main Image */}
                            <div className='relative bg-white rounded-lg mb-3 lg:mb-4'>
                                <img 
                                    src={`${SERVER}/${selectedImage || selectedProduct.banner_image}`} 
                                    alt={selectedProduct.title}
                                    className="w-full h-auto object-contain max-h-[300px] lg:max-h-[500px]"
                                />
                                
                                {/* Mobile Navigation Arrows - Only show if there are thumbnail images */}
                                {selectedProduct.images?.length > 0 && (
                                    <>
                                        {/* Left Arrow */}
                                        <button 
                                            className='lg:hidden absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 rounded-full shadow-md border border-gray-200'
                                            onClick={() => {
                                                const currentIndex = selectedProduct.images.findIndex(img => img === selectedImage);
                                                const prevIndex = currentIndex <= 0 ? selectedProduct.images.length - 1 : currentIndex - 1;
                                                setSelectedImage(selectedProduct.images[prevIndex]);
                                            }}
                                        >
                                            <IoChevronForward className='rotate-180 text-gray-600' size={20} />
                                        </button>

                                        {/* Right Arrow */}
                                        <button 
                                            className='lg:hidden absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 rounded-full shadow-md border border-gray-200'
                                            onClick={() => {
                                                const currentIndex = selectedProduct.images.findIndex(img => img === selectedImage);
                                                const nextIndex = (currentIndex + 1) % selectedProduct.images.length;
                                                setSelectedImage(selectedProduct.images[nextIndex]);
                                            }}
                                        >
                                            <IoChevronForward className='text-gray-600' size={20} />
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Thumbnail Gallery */}
                            {selectedProduct.images?.length > 0 && (
                                <div className='relative flex items-center justify-center gap-2 lg:gap-4'>
                                    {/* Left Arrow - Hidden on mobile */}
                                    <button 
                                        className='hidden lg:flex w-12 h-12 items-center justify-center bg-white rounded-full shadow-md hover:shadow-lg transition-shadow border border-gray-200 hover:border-primary'
                                        onClick={() => {
                                            const gallery = document.getElementById('thumbnail-gallery');
                                            gallery.scrollBy({ left: -200, behavior: 'smooth' });
                                        }}
                                    >
                                        <IoChevronForward className='rotate-180 text-gray-600' size={24} />
                                    </button>

                                    {/* Thumbnails Container */}
                                    <div 
                                        id='thumbnail-gallery'
                                        className='flex gap-2 lg:gap-4 overflow-x-auto flex-1 lg:flex-auto scrollbar-hide justify-center'
                                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                                    >
                                        {/* Thumbnail Images */}
                                        {selectedProduct.images.map((image, index) => (
                                            <div 
                                                key={index} 
                                                className={`flex-shrink-0 w-20 h-20 lg:w-36 lg:h-36 border-2 ${
                                                    selectedImage === image 
                                                        ? 'border-primary' 
                                                        : 'border-gray-100'
                                                } rounded-lg lg:rounded-xl overflow-hidden cursor-pointer hover:border-primary transition-all hover:shadow-md`}
                                                onClick={() => setSelectedImage(image)}
                                            >
                                                <img 
                                                    src={`${SERVER}/${image}`} 
                                                    alt={`Product view ${index + 1}`}
                                                    className="w-full h-full object-contain p-1 lg:p-2"
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Right Arrow - Hidden on mobile */}
                                    <button 
                                        className='hidden lg:flex w-12 h-12 items-center justify-center bg-white rounded-full shadow-md hover:shadow-lg transition-shadow border border-gray-200 hover:border-primary'
                                        onClick={() => {
                                            const gallery = document.getElementById('thumbnail-gallery');
                                            gallery.scrollBy({ left: 200, behavior: 'smooth' });
                                        }}
                                    >
                                        <IoChevronForward className='text-gray-600' size={24} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Right: Product Details */}
                        <div className='flex-1'>
                            {/* Product Title */}
                            <h1 className="text-xl lg:text-3xl font-bold text-black mb-3 lg:mb-4">{selectedProduct.title}</h1>

                            {/* Badges */}
                            <div className='flex gap-2 mb-3 lg:mb-4'>
                                {selectedProduct?.promotion > 0 && (
                                    <span className="bg-red-500 text-white text-xs lg:text-sm font-normal px-2 lg:px-3 py-0.5 lg:py-1 rounded">
                                        {formatPrice(selectedProduct.promotion, 0)}% {t('product.discount')}
                                    </span>
                                )}
                                {selectedProduct.nouveaute && (
                                    <span className="bg-yellow-400 text-black text-xs lg:text-sm font-normal px-2 lg:px-3 py-0.5 lg:py-1 rounded">
                                        {t('product.newBadge')}
                                    </span>
                                )}
                            </div>

                            {/* Price */}
                            {parseFloat(selectedProduct?.promotion) > 0 ? (
                                <div className='flex items-center gap-2 lg:gap-3 mb-4 lg:mb-6'>
                                    <span className="text-2xl lg:text-3xl font-bold text-red-500">
                                        € {formatPrice(selectedProduct.price * (1 - selectedProduct.promotion / 100))}
                                    </span>
                                    <span className="text-base lg:text-xl text-gray-400 line-through">
                                        € {formatPrice(selectedProduct.price)}
                                    </span>
                                </div>
                            ) : (
                                <div className='mb-4 lg:mb-6'>
                                    <span className="text-2xl lg:text-3xl font-bold text-primary">
                                        € {formatPrice(selectedProduct?.price)}
                                    </span>
                                </div>
                            )}

                            {/* Description */}
                            <ul className="list-disc pl-4 lg:pl-5 mb-4 lg:mb-6 space-y-1 lg:space-y-2 text-sm lg:text-base text-black">
                                {selectedProduct.description.split('\r\n').map((line, index) => (
                                    line.trim() && <li key={index}>{line.trim()}</li>
                                ))}
                            </ul>

                            {/* Quantity */}
                            <div className="mb-4 lg:mb-6">
                                <label className="block text-semibold font-meduim text-black mb-2 lg:mb-4 text-sm lg:text-base">
                                    {t('product.quantity')}
                                </label>
                                <div className="inline-flex items-center border border-black rounded-full px-2 py-1 lg:py-2">
                                    <button 
                                        onClick={() => updateQuantity(Math.max(1, quantite - 1))}
                                        disabled={quantite <= 1}
                                        className="text-lg lg:text-xl font-bold disabled:opacity-30 disabled:cursor-not-allowed hover:text-primary transition-colors px-2"
                                    >
                                        −
                                    </button>
                                    <span className="mx-3 lg:mx-4 text-lg lg:text-xl font-normal min-w-[20px] text-center">{quantite}</span>
                                    <button 
                                        onClick={() => updateQuantity(quantite + 1)}
                                        className="text-lg lg:text-xl font-bold hover:text-primary transition-colors px-2"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            {/* Calibration Checkbox */}
                            {selectedProduct.calibrage && parseFloat(selectedProduct.calibrage) > 0 && (
                                <div className='mb-4 lg:mb-6'>
                                    <label className='flex items-center justify-between p-3 lg:p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-primary transition-colors'>
                                        <div className='flex items-center gap-2 lg:gap-3'>
                                            <input
                                                type="checkbox"
                                                checked={chosenCalibrage}
                                                onChange={(e) => setChosenCalibrage(e.target.checked)}
                                                className='w-4 h-4 lg:w-5 lg:h-5 rounded cursor-pointer'
                                                style={{ accentColor: '#1B66A4' }}
                                            />
                                            <span className='text-sm lg:text-base font-medium text-black'>{t('product.calibration')}</span>
                                            <InfoIconWithTooltip />
                                        </div>
                                        <span className='text-lg lg:text-xl font-bold text-black'>€ {formatPrice(selectedProduct.calibrage)}</span>
                                    </label>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className='flex gap-3 lg:gap-4 mb-4'>
                                <button 
                                    onClick={() => addToCart(selectedProduct, quantite, chosenCalibrage ? selectedProduct.calibrage : 0)}
                                    className="flex-1 bg-primary text-white py-2.5 lg:py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors text-base lg:text-lg"
                                >
                                    {t('product.addToCart')}
                                </button>
                                <button 
                                    onClick={toggleWishlist}
                                    className="w-12 h-10 lg:w-14 lg:h-12 flex items-center justify-center border border-black rounded-lg hover:border-primary transition-colors"
                                >
                                    {isInWishlist(selectedProduct.id) ? (
                                        <FaHeart className="text-red-500" size={20} />
                                    ) : (
                                        <FaRegHeart className="text-black" size={20} />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* Tabs */}
                    <div className='lg:flex lg:justify-between lg:border-b lg:border-gray-200 mb-4 lg:mb-6 max-w-4xl mx-auto'>
                        {/* Mobile Design - Accordion style with content under each tab */}
                        <div className='lg:hidden space-y-0'>
                            {/* Caracteristique Tab */}
                            <div className='border-t border-gray-400'>
                                <button 
                                    className='w-full text-left px-0 py-4 font-normal text-base text-black border-b border-gray-400'
                                    onClick={() => { setActiveTab(activeTab === 'caracteristique' ? '' : 'caracteristique'); }}
                                >
                                    {t('product.characteristics')}
                                </button>
                                {activeTab === 'caracteristique' && (
                                    <div className='py-4 border-b border-gray-400'>
                                        <div className='space-y-0'>
                                            {selectedProduct.secondary_caracteristiques && Object.values(selectedProduct.secondary_caracteristiques).map((element, index) => (
                                                <div 
                                                    key={index}
                                                    className={`flex items-center justify-between py-2.5 border-b border-gray-100 ${
                                                        index % 2 === 0 ? 'bg-gray-50' : ''
                                                    } px-2`}
                                                >
                                                    <span className='text-black font-medium text-sm'>
                                                        {(i18n.language === 'en' && element.title_en) ? element.title_en : element.title}
                                                    </span>
                                                    <span className='text-gray-900 font-normal text-sm'>
                                                        {Array.isArray(element.value) ? (
                                                            element.value.join(', ')
                                                        ) : element.value !== undefined && element.value !== null && element.value !== '' ? (
                                                            <>
                                                                {element.value?.toLowerCase() === 'oui' ?
                                                                    <FaCheck className="text-green-500" />
                                                                : element.value?.toLowerCase() === 'non' ? (
                                                                    <FaXmark className="text-red-500" />
                                                                ) : (
                                                                    <>
                                                                        {element.value}
                                                                        {(i18n.language === 'en' && element.unity_en) ? ` ${element.unity_en}` : element.unity ? ` ${element.unity}` : ''}
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Documents Tab */}
                            <div>
                                <button 
                                    className='w-full text-left px-0 py-4 font-normal text-base text-black border-b border-gray-400'
                                    onClick={() => { setActiveTab(activeTab === 'docs' ? '' : 'docs'); }}
                                >
                                    {t('product.downloadDocuments')}
                                </button>
                                {activeTab === 'docs' && (
                                    <div className='py-4 border-b border-gray-400'>
                                        {selectedProduct.documents?.length > 0 ? (
                                            <div className='grid gap-3'>
                                                {selectedProduct.documents.map((doc) => (
                                                    <div 
                                                        key={doc.id} 
                                                        className='flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-primary cursor-pointer transition-colors'
                                                        onClick={() => {
                                                            window.open(`${SERVER}/${doc.file_path.replace(/\\/g, '/')}`, '_blank');
                                                        }}
                                                    >
                                                        <FaFilePdf size={24} className="text-red-500" />
                                                        <span className='font-medium text-gray-900 text-sm'>{doc.title}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className='text-center py-8 text-gray-500 text-sm'>
                                                {t('product.noDocuments')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Accessoires Tab */}
                            <div>
                                <button 
                                    className='w-full text-left px-0 py-4 font-normal text-base text-black border-b border-gray-400'
                                    onClick={() => { setActiveTab(activeTab === 'acc' ? '' : 'acc'); }}
                                >
                                    {t('product.compatibleAccessories')}
                                </button>
                                {activeTab === 'acc' && (
                                    <div className='py-4'>
                                        {selectedProduct.related_products?.length > 0 ? (
                                            <div className="grid gap-3">
                                                {selectedProduct.related_products.map((product) => (
                                                    <div 
                                                        key={product.id} 
                                                        className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-primary cursor-pointer transition-colors"
                                                        onClick={() => {
                                                            navigate(`/product/${categoryTitle}/${subCategoryTitle}/${product.title}`, { 
                                                                state: { categoryId: categoryId, subCategoryId: subCategoryId, productId: product.id } 
                                                            });
                                                            window.scrollTo(0, 0);
                                                        }}
                                                    >
                                                        <div className="w-16 h-16 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
                                                            {product.banner_image && (
                                                                <img 
                                                                    src={`${SERVER}/${product.banner_image}`} 
                                                                    alt={product.title}
                                                                    className="w-full h-full object-contain"
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="flex-1">
                                                            <h4 className="font-normal text-gray-900 mb-1 text-sm">{product.title}</h4>
                                                            <div className="flex items-center gap-2">
                                                                {product.promotion > 0 ? (
                                                                    <>
                                                                        <span className="text-base font-bold text-red-500">
                                                                            €{formatPrice(product.price * (1 - product.promotion / 100))}
                                                                        </span>
                                                                        <span className="text-xs text-gray-400 line-through">
                                                                            €{formatPrice(product.price)}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-base font-bold text-primary">
                                                                        €{formatPrice(product.price)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <button 
                                                            className="bg-primary text-white p-2.5 rounded-lg hover:bg-blue-700 transition-colors"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                addToCart(product);
                                                            }}
                                                        >
                                                            <PiShoppingCartSimple size={18} />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8 text-gray-500 text-sm">
                                                {t('product.noRelatedProducts')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Desktop Design - Horizontal tabs with content below */}
                        <div className='hidden lg:block lg:w-full'>
                            <div className='flex border-b border-gray-200'>
                                <button 
                                    className={`px-6 py-3 font-normal transition-colors text-base whitespace-nowrap ${
                                        activeTab === 'caracteristique' 
                                            ? 'text-primary border-b-2 border-primary' 
                                            : 'text-black hover:text-primary'
                                    }`}
                                    onClick={() => { setActiveTab('caracteristique'); }}
                                >
                                    {t('product.characteristics')}
                                </button>
                                <button 
                                    className={`px-6 py-3 font-normal transition-colors text-base whitespace-nowrap ${
                                        activeTab === 'docs' 
                                            ? 'text-primary border-b-2 border-primary' 
                                            : 'text-black hover:text-primary'
                                    }`}
                                    onClick={() => { setActiveTab('docs'); }}
                                >
                                    {t('product.downloadDocuments')}
                                </button>
                                <button 
                                    className={`px-6 py-3 font-normal transition-colors text-base whitespace-nowrap ${
                                        activeTab === 'acc' 
                                            ? 'text-primary border-b-2 border-primary' 
                                            : 'text-black hover:text-primary'
                                    }`}
                                    onClick={() => { setActiveTab('acc');}}
                                >
                                    {t('product.compatibleAccessories')}
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* Tab Content - Desktop Only */}
                    <div className='hidden lg:block'>
                    {activeTab === 'caracteristique' ? (
                        <div className='max-w-4xl mx-auto bg-white rounded-xl p-6 shadow-sm'>
                            <div className='space-y-0'>
                                {selectedProduct.secondary_caracteristiques && Object.values(selectedProduct.secondary_caracteristiques).map((element, index) => (
                                    <div 
                                        key={index}
                                        className={`flex items-center justify-between py-4 border-b border-gray-100 ${
                                            index % 2 === 0 ? 'bg-gray-50' : ''
                                        } px-4`}
                                    >
                                        <span className='text-black font-medium text-base'>
                                            {(i18n.language === 'en' && element.title_en) ? element.title_en : element.title}
                                        </span>
                                        <span className='text-gray-900 font-normal text-base'>
                                            {Array.isArray(element.value) ? (
                                                element.value.join(', ')
                                            ) : element.value !== undefined && element.value !== null && element.value !== '' ? (
                                                <>
                                                    {element.value?.toLowerCase() === 'oui' ?
                                                        <FaCheck className="text-green-500" />
                                                    : element.value?.toLowerCase() === 'non' ? (
                                                        <FaXmark className="text-red-500" />
                                                    ) : (
                                                        <>
                                                            {element.value}
                                                            {(i18n.language === 'en' && element.unity_en) ? ` ${element.unity_en}` : element.unity ? ` ${element.unity}` : ''}
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : activeTab === 'docs' ? (
                        <div className='max-w-4xl mx-auto bg-white rounded-xl p-6 shadow-sm'>
                            {selectedProduct.documents?.length > 0 ? (
                                <div className='grid gap-4'>
                                    {selectedProduct.documents.map((doc) => (
                                        <div 
                                            key={doc.id} 
                                            className='flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary cursor-pointer transition-colors'
                                            onClick={() => {
                                                window.open(`${SERVER}/${doc.file_path.replace(/\\/g, '/')}`, '_blank');
                                            }}
                                        >
                                            <FaFilePdf size={24} className="text-red-500 lg:w-8 lg:h-8" />
                                            <span className='font-medium text-gray-900 text-base'>{doc.title}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className='text-center py-8 text-gray-500 text-base'>
                                    {t('product.noDocuments')}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className='max-w-4xl mx-auto bg-white rounded-xl p-6 shadow-sm'>
                            {selectedProduct.related_products?.length > 0 ? (
                                <div className="grid gap-4">
                                    {selectedProduct.related_products.map((product) => (
                                        <div 
                                            key={product.id} 
                                            className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary cursor-pointer transition-colors"
                                            onClick={() => {
                                                navigate(`/product/${categoryTitle}/${subCategoryTitle}/${product.title}`, { 
                                                    state: { categoryId: categoryId, subCategoryId: subCategoryId, productId: product.id } 
                                                });
                                                window.scrollTo(0, 0);
                                            }}
                                        >
                                            <div className="w-20 h-20 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
                                                {product.banner_image && (
                                                    <img 
                                                        src={`${SERVER}/${product.banner_image}`} 
                                                        alt={product.title}
                                                        className="w-full h-full object-contain"
                                                    />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-normal text-gray-900 mb-1 text-base">{product.title}</h4>
                                                <div className="flex items-center gap-2">
                                                    {product.promotion > 0 ? (
                                                        <>
                                                            <span className="text-lg font-bold text-red-500">
                                                                €{formatPrice(product.price * (1 - product.promotion / 100))}
                                                            </span>
                                                            <span className="text-sm text-gray-400 line-through">
                                                                €{formatPrice(product.price)}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="text-lg font-bold text-primary">
                                                            €{formatPrice(product.price)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button 
                                                className="bg-primary text-white p-3 rounded-lg hover:bg-blue-700 transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    addToCart(product);
                                                }}
                                            >
                                                <PiShoppingCartSimple size={18} className="lg:w-5 lg:h-5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500 text-base">
                                    {t('product.noRelatedProducts')}
                                </div>
                            )}
                        </div>
                    )}
                    </div>
                </div>
            )}
            {selectedImage && (
                <div className='image-modal' onClick={() => setSelectedImage(null)}>
                    <div className='modal-content'>
                    <img 
                        src={`${SERVER}/${selectedImage}`} 
                        alt="Enlarged product view"
                    />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Product;